<template>
  <div class="aws-container">
    <div class="aws-inner-container">
      <div class="aws-input-container" style="margin-bottom: 0">
        <input
          id="esriAddress"
          class="aws-search-address"
          type="text"
          :placeholder="placeHolder"
          v-model="searchAddress.userInput"
          :disabled="isResolving"
        />
        <div class="custom-button-container">
          <button @click="customAddressToggle()" v-if="toggleCustomAddressButton">Custom Address</button>
        </div>
      </div>

      <div class="auto-complete-result-container">
        <div class="autocomplete-result" id="esri-autocomplete-result" v-if="suggestions.length > 0 && !isResolving">
          <p
            @click="selectAddress(index)"
            v-for="(suggestion, index) in suggestions"
            :key="index"
          >
            <i class="fa fa-map-pin"></i>{{ suggestion.text }}
          </p>
          <p @click="customAddressToggle()">No results found? Click here.</p>
        </div>
        <div class="autocomplete-result" id="esri-autocomplete-result" v-if="suggestions.length === 0 && noResults && !isResolving && !errorMessage">
          <p @click="customAddressToggle()">No results found? Click here.</p>
        </div>
        <div class="autocomplete-result" v-if="isResolving">
          <p class="resolving-message"><i class="fa fa-spinner fa-spin"></i> Resolving address...</p>
        </div>
        <div class="autocomplete-result error-result" v-if="errorMessage">
          <p class="error-message"><i class="fa fa-exclamation-triangle"></i> {{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API } from "aws-amplify";

const suggestAddressQuery = `query suggestAddress($text: String!, $biasPosition: String) {
  suggestAddress(text: $text, biasPosition: $biasPosition) {
    suggestions {
      text
      magicKey
      isCollection
    }
    error
  }
}`;

const resolvePlaceMutation = `mutation resolvePlace($input: ResolvePlaceInput!) {
  resolvePlace(input: $input) {
    placeID
    placeCode
    latitude
    longitude
    relevance
    address1
    address2
    city
    state
    zipCode
    serviceAddressType
    operatorTitle
    timeWindowStart
    timeWindowEnd
    timeWindowType
    deliveryWindowStart
    deliveryWindowEnd
    deliveryWindowLabel
    pickupWindowStart
    pickupWindowEnd
    pickupWindowLabel
  }
}`;

export default {
  props: ["placeHolder", "authMode"],
  data() {
    return {
      searchAddress: {
        userInput: "",
      },
      suggestions: [],
      toggleCustomAddressButton: false,
      debounceTimer: null,
      isResolving: false,
      errorMessage: "",
      noResults: false,
    };
  },
  methods: {
    /**
     * Calls the suggestAddress GraphQL query through the AppSync proxy.
     * Handles TOKEN_REFRESHING error by retrying after 2 seconds.
     */
    async fetchSuggestions() {
      if (this.searchAddress.userInput.length <= 3) {
        this.suggestions = [];
        return;
      }

      try {
        const graphqlOptions = {
          query: suggestAddressQuery,
          variables: { text: this.searchAddress.userInput },
        };
        if (this.authMode) graphqlOptions.authMode = this.authMode;
        const response = await API.graphql(graphqlOptions);

        const result = response.data.suggestAddress;

        if (result.error === "TOKEN_REFRESHING") {
          setTimeout(() => {
            this.fetchSuggestions();
          }, 2000);
          return;
        }

        if (result.error) {
          this.errorMessage = result.error;
          this.suggestions = [];
          return;
        }

        this.errorMessage = "";
        if (result.suggestions && result.suggestions.length > 0) {
          this.suggestions = result.suggestions;
          this.noResults = false;
        } else {
          this.suggestions = [];
          this.noResults = true;
        }
      } catch (err) {
        console.error("Error fetching suggestions:", err);
        this.errorMessage = err.errors?.[0]?.message || "Failed to fetch suggestions";
        this.suggestions = [];
      }
    },

    /**
     * When the user selects a suggestion, call CorePlaceAddress via the
     * resolvePlace mutation to get full geocoded/cached place data.
     */
    async selectAddress(index) {
      const suggestion = this.suggestions[index];
      if (!suggestion || !suggestion.text) {
        this.$emit("GetSelectedAddressData", false);
        return;
      }

      this.isResolving = true;
      this.suggestions = [];

      try {
        const graphqlOptions = {
          query: resolvePlaceMutation,
          variables: { input: { fullAddress: suggestion.text } },
        };
        if (this.authMode) graphqlOptions.authMode = this.authMode;
        const response = await API.graphql(graphqlOptions);

        const place = response.data.resolvePlace;

        if (!place || (!place.placeID && place.relevance === "0")) {
          // Zero-candidate response — resolution failed
          this.$emit("GetSelectedAddressData", false);
          this.isResolving = false;
          this.searchAddress.userInput = "";
          return;
        }

        const addressData = {
          address1: place.address1 || "",
          city: place.city || "",
          state: place.state || "",
          zipCode: place.zipCode || "",
          plusFour: "",
          latitude: place.latitude || null,
          longitude: place.longitude || null,
          relevance: place.relevance ? parseFloat(place.relevance) : 0,
          placeID: place.placeID || "",
          placeCode: place.placeCode || "",
          serviceAddressType: place.serviceAddressType || "",
          operatorTitle: place.operatorTitle || "",
          timeWindowStart: place.timeWindowStart || "",
          timeWindowEnd: place.timeWindowEnd || "",
          timeWindowType: place.timeWindowType || "",
          deliveryWindowStart: place.deliveryWindowStart || "",
          deliveryWindowEnd: place.deliveryWindowEnd || "",
          deliveryWindowLabel: place.deliveryWindowLabel || "",
          pickupWindowStart: place.pickupWindowStart || "",
          pickupWindowEnd: place.pickupWindowEnd || "",
          pickupWindowLabel: place.pickupWindowLabel || "",
        };

        this.$emit("GetSelectedAddressData", addressData);
      } catch (err) {
        console.error("Error resolving place:", err);
        this.$emit("GetSelectedAddressData", false);
      }

      this.isResolving = false;
      this.searchAddress.userInput = "";
    },

    customAddressToggle() {
      this.$emit("customAddressToggle", true);
    },
  },
  watch: {
    "searchAddress.userInput": function () {
      if (this.searchAddress.userInput.length <= 3) {
        this.suggestions = [];
        this.toggleCustomAddressButton = false;
        this.errorMessage = "";
        this.noResults = false;
        return;
      }

      this.toggleCustomAddressButton = true;

      // 300ms debounce
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.fetchSuggestions();
      }, 300);
    },
  },
  mounted() {
    var vueInstance = this;
    document.addEventListener("click", function clickOutsideEsriAutoComplete(event) {
      const autocompleteBox = document.getElementById("esriAddress");
      if (vueInstance.searchAddress.userInput.length > 0) {
        if (autocompleteBox && !autocompleteBox.contains(event.target)) {
          vueInstance.searchAddress.userInput = "";
          vueInstance.suggestions = [];
        }
      }
    });
  },
  beforeMount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
};
</script>

<style scoped>
.aws-input-container {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.custom-button-container {
  margin: 1px;
}

.custom-button-container button {
  padding-top: 2.5px;
  padding-bottom: 2.5px;
  padding-right: 5px;
  padding-left: 5px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 12px;
  color: #fff;
  background-color: #32ccfe;
}

.aws-search-address {
  padding: 10px 0px 10px 10px;
  font-size: 15px;
  border: 1px solid rgba(0, 0, 0, 0.336);
  border-radius: 5px;
  outline: none;
  width: 100%;
  margin-bottom: 0;
}

.fa-map-pin {
  color: #999;
  margin-right: 5px;
  font-size: 1em;
}

.autocomplete-result {
  background-color: #fff;
  border-radius: 5px;
  text-align: left;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.062);
  margin-bottom: 10px;
  position: absolute;
  z-index: 10;
}

@keyframes animate-result {
  from {
    margin-top: -5px;
  }
  to {
    margin-top: 0;
  }
}

.autocomplete-result p {
  cursor: pointer;
  transition-duration: 0.5s;
  margin: 0;
  padding: 10px;
  font-size: 12px;
  animation: animate-result 0.5s ease;
  border-bottom: 1px solid rgb(235, 235, 235);
}

.autocomplete-result p:hover {
  background-color: rgb(235, 235, 235);
  transition-duration: 0.5s;
}

.resolving-message {
  cursor: default;
  color: #666;
}

.resolving-message:hover {
  background-color: #fff;
}

.error-result {
  border: 1px solid #f5c6cb;
  background-color: #fff3f3;
}

.error-message {
  color: #c0392b;
  cursor: default;
}

.error-message:hover {
  background-color: #fff3f3;
}

.fa-exclamation-triangle {
  color: #c0392b;
  margin-right: 5px;
}
</style>
